import { useEffect, useState } from 'react'
import { pruefePasswort } from '@raporto/shared'
import { Passwortfeld } from '../ui/Passwortfeld.js'
import { Marke } from './Marke.js'
import { api } from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'

/**
 * Ein Passwort über einen Link setzen – nach «Passwort vergessen» oder nach
 * einer Einladung.
 *
 * Ein Bildschirm für beides, weil es aus Sicht der Person derselbe Vorgang
 * ist: sie hat eine Mail bekommen, klickt, und tippt ein Passwort. Was sich
 * unterscheidet, sind drei Sätze – und die stehen unten in einer Tabelle
 * statt in zwei fast gleichen Dateien, die eines Tages auseinanderlaufen.
 *
 * Drei Dinge, die hier nicht passieren:
 *
 *   Der Link wird beim Öffnen NICHT verbraucht. Sonst entwertet jede Vorschau
 *   in einem Mailprogramm den Zugang, und die Person steht vor einer Seite,
 *   die sagt, ihr eigener Link sei schon benutzt.
 *
 *   Nach dem Setzen wird NICHT automatisch angemeldet. Wer sein Passwort
 *   zurücksetzt, soll es einmal eintippen – das ist die Probe darauf, dass er
 *   es sich gemerkt hat, und es hält den Vorgang an einer Stelle.
 *
 *   Der Grund für einen ungültigen Link wird nicht genannt. «Abgelaufen»
 *   verriete, dass es diesen Link einmal gab.
 */

type Art = 'passwort' | 'einladung'


const TEXTE: Record<Art, { titel: string; unter: string; knopf: string; fertig: string }> = {
  passwort: {
    titel: 'Neues Passwort setzen',
    unter: 'Danach wirst du auf allen Geräten neu angemeldet.',
    knopf: 'Passwort setzen',
    fertig: 'Das Passwort ist gesetzt. Alle bisherigen Anmeldungen wurden beendet.'
  },
  einladung: {
    titel: 'Willkommen bei Raporto',
    unter: 'Setz dein eigenes Passwort. Niemand sonst kennt es danach – auch nicht, wer dich eingeladen hat.',
    knopf: 'Zugang einrichten',
    fertig: 'Dein Zugang ist eingerichtet.'
  }
}

export function Zugang({
  art,
  token,
  aufLogin
}: {
  art: Art
  token: string
  aufLogin: () => void
}): JSX.Element {
  const [stand, setStand] = useState<'pruefe' | 'offen' | 'ungueltig' | 'fertig'>('pruefe')
  const [name, setName] = useState('')
  const [passwort, setPasswort] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    void api
      .zugangPruefen(art, token)
      .then((a) => {
        setName(a.name)
        setStand(a.gueltig ? 'offen' : 'ungueltig')
      })
      .catch(() => setStand('ungueltig'))
  }, [art, token])

  const ungleich = wiederholung.length > 0 && passwort !== wiederholung

  async function setzen(): Promise<void> {
    setFehler(null)
    if (passwort.length < 12) return setFehler('Das Passwort braucht mindestens 12 Zeichen.')
    if (passwort !== wiederholung) return setFehler('Die beiden Eingaben sind nicht gleich.')
    setLaeuft(true)
    try {
      if (art === 'passwort') await api.passwortNeuSetzen(token, passwort)
      else await api.einladungAnnehmen(token, passwort)
      setStand('fertig')
    } catch (e) {
      setFehler(fehlerText(e))
    } finally {
      setLaeuft(false)
    }
  }

  const texte = TEXTE[art]

  /*
    Dieselbe Seite wie die Anmeldung, und zwar wörtlich: `rp-pg-anmeldung`,
    Markenspalte links, Karte rechts.

    Hier stand `rp-public rp-login` mit `rp-login-karte`. Die beiden letzten
    Klassen gibt es in keiner Stilvorlage – die Seite rendete also als
    nackter Kasten mit Browserstandard. Das fiel nie auf, weil man hier nur
    über einen Link aus einer Mail landet: nicht beim Entwickeln, sondern am
    heikelsten Punkt des ganzen Wegs, beim Setzen des eigenen Passworts.
  */
  const links = (satz: string, punkte: string[]): JSX.Element => (
    <aside className="rp-an-marke">
      <div className="rp-an-knopfzeile">
        <button className="rp-pg-marke" type="button" onClick={aufLogin} id="btn-marke">
          <Marke groesse={30} />
        </button>
      </div>
      <p className="rp-an-satz">{satz}</p>
      <ul className="rp-an-punkte">
        {punkte.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    </aside>
  )

  const punkte =
    art === 'einladung'
      ? [
          'Dein Passwort kennt danach niemand sonst',
          'Auch nicht, wer dich eingeladen hat',
          'Mindestens zwölf Zeichen'
        ]
      : [
          'Alle bisherigen Anmeldungen werden beendet',
          'Der Link gilt einmal',
          'Mindestens zwölf Zeichen'
        ]

  return (
    <div className="rp-pg rp-pg-anmeldung" id={`zugang-${art}`}>
      <div className="rp-an">
        {links(
          stand === 'ungueltig'
            ? 'Ein Link aus einer Mail gilt eine Weile, nicht ewig.'
            : art === 'einladung'
              ? 'Noch ein Schritt, dann gehört der Zugang dir.'
              : 'Ein neues Passwort, und du bist wieder drin.',
          stand === 'ungueltig' ? [] : punkte
        )}

        <main className="rp-an-seite">
          {/*
            Im offenen Zustand IST das Formular die Karte – wie in
            `Login.tsx`, und nicht aus Stiltreue: `.rp-an-karte` ordnet seine
            Kinder mit `gap`. Steckt das Formular als einzelnes Kind darin,
            gilt der Abstand zwischen Formular und Nichts, und innerhalb
            kleben Überschrift, Satz und Felder aneinander.
          */}
          {stand === 'offen' ? (
            <form
              className="rp-an-karte"
              onSubmit={(e) => {
                e.preventDefault()
                void setzen()
              }}
            >
              <span className="rp-strahl" aria-hidden="true">
                <span />
              </span>

              <h1>{texte.titel}</h1>
              <p className="rp-an-unter">
                {name ? `Hallo ${name}. ` : ''}
                {texte.unter}
              </p>

              {fehler && (
                <p className="rp-an-fehler" id="zugang-fehler">
                  {fehler}
                </p>
              )}

              <Passwortfeld
                id="zugang-pw"
                stil="anmeldung"
                label="Neues Passwort"
                wert={passwort}
                aufWert={setPasswort}
                umfeld={{ name }}
                autoFocus
              />

              <div className="rp-an-feld">
                <label htmlFor="zugang-pw2">Noch einmal</label>
                <div className="rp-an-eingabe">
                  <input
                    id="zugang-pw2"
                    type="password"
                    autoComplete="new-password"
                    value={wiederholung}
                    onChange={(e) => setWiederholung(e.target.value)}
                  />
                </div>
                {ungleich && (
                  <span className="rp-an-fehler">Die beiden stimmen nicht überein.</span>
                )}
              </div>

              <button
                className="rp-pg-knopf rp-pg-knopf-hell"
                id="btn-zugang-setzen"
                type="submit"
                disabled={
                  laeuft || !pruefePasswort(passwort, { name }).ok || passwort !== wiederholung
                }
              >
                {laeuft ? 'Einen Moment …' : texte.knopf}
              </button>
            </form>
          ) : (
          <div className="rp-an-karte">
            <span className="rp-strahl" aria-hidden="true">
              <span />
            </span>

            {stand === 'pruefe' && (
              <>
                <h1>Einen Moment …</h1>
                <p className="rp-an-unter">Wir prüfen deinen Link.</p>
              </>
            )}

            {stand === 'ungueltig' && (
              <>
                <h1>Dieser Link gilt nicht mehr</h1>
                <p className="rp-an-unter">
                  Links aus einer E-Mail gelten nur eine begrenzte Zeit und nur einmal.
                  Fordere über die Anmeldung einen neuen an – oder bitte jemanden aus
                  deinem Betrieb darum.
                </p>
                <button
                  type="button"
                  className="rp-pg-knopf rp-pg-knopf-hell"
                  id="btn-zugang-login"
                  onClick={aufLogin}
                >
                  Zur Anmeldung
                </button>
              </>
            )}

            {stand === 'fertig' && (
              <>
                <h1>Fertig</h1>
                <p className="rp-an-unter">{texte.fertig}</p>
                <button
                  type="button"
                  className="rp-pg-knopf rp-pg-knopf-hell"
                  id="btn-zugang-weiter"
                  onClick={aufLogin}
                >
                  Jetzt anmelden
                </button>
              </>
            )}

          </div>
          )}
        </main>
      </div>
    </div>
  )
}
